import { create } from 'zustand';
import { usePatientsStore } from './patients';
import { useDoctorsStore } from './doctors';
import { format, startOfDay, endOfDay, isSameDay, differenceInDays } from 'date-fns';
import { supabase } from '@/lib/supabase';

interface AnalyticsState {
  getRevenueSummary: () => Promise<{
    totalRevenue: number;
    doctorRevenue: { doctorId: string; doctorName: string; revenue: number }[];
    dateRevenue: { date: string; revenue: number }[];
    paymentMethodRevenue: { method: string; revenue: number }[];
    serviceRevenue: { serviceName: string; revenue: number; count: number }[];
    presentMonthServiceRevenue: { serviceName: string; revenue: number; count: number }[];
    previousMonthServiceRevenue: { serviceName: string; revenue: number; count: number }[];
    monthlyTrend: { month: string; revenue: number; patients: number }[];
    revenueForecast: { date: string; forecast: number }[];
    dailyRevenueData: { day: string; currentMonth: number; previousMonth: number }[];
    dailyPatientData: { day: string; currentMonth: number; previousMonth: number }[];
    growthMetrics: {
      revenueGrowth: number;
      patientGrowth: number;
      avgRevenuePerPatient: number;
    };
    retentionMetrics: {
      returningPatients: number;
      retentionRate: number;
      avgDaysBetweenVisits: number;
    };
    doctorKPIs: {
      doctorId: string;
      doctorName: string;
      avgRevenuePerPatient: number;
      patientSatisfaction: number;
      efficiency: number;
    }[];
    timeAnalytics: {
      peakHours: { hour: number; count: number }[];
      peakDays: { day: string; count: number }[];
      avgWaitTime: number;
    };
  }>;
  getPatientCountByDoctor: () => { doctorId: string; doctorName: string; count: number }[];
  getCompletedAppointmentsByDate: (startDate?: Date, endDate?: Date) => {
    date: string;
    count: number;
  }[];
}

export const useAnalyticsStore = create<AnalyticsState>()((get) => ({
  getRevenueSummary: async () => {
    try {
      const { patients } = usePatientsStore.getState();
      const { doctors } = useDoctorsStore.getState();
      
      // Helper function to get patient revenue from payment history
      const getPatientRevenue = (patient: any): number => {
        const paymentHistoryTotal = (patient.paymentHistory || []).reduce((total: number, payment: any) => 
          total + (Number(payment.amount) || 0), 0);
        return paymentHistoryTotal > 0 ? paymentHistoryTotal : (patient.amountPaid || 0);
      };
      
      // Helper function to check if patient has any payments
      const hasPayments = (patient: any): boolean => {
        const hasPaymentHistory = (patient.paymentHistory || []).length > 0;
        const hasAmountPaid = (patient.amountPaid || 0) > 0;
        return hasPaymentHistory || hasAmountPaid;
      };
      
      // Filter patients with payments (regardless of status) - for revenue calculations
      const patientsWithPayments = patients.filter(hasPayments);
      
      // Keep completedPatients for patient count metrics (workflow-related, not revenue)
      const completedPatients = patients.filter(p => p.status === "completed");
      
      // Get current and previous month dates
      const currentDate = new Date();
      const currentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const previousMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
      
      // Calculate total revenue from payment history (all patients with payments, regardless of status)
      const totalRevenue = patientsWithPayments.reduce((sum, p) => {
        return sum + getPatientRevenue(p);
      }, 0);
      
      // Calculate revenue by doctor (all patients with payments, regardless of status)
      const doctorRevenue = doctors.map(doctor => {
        const doctorPatients = patientsWithPayments.filter(p => p.doctorId === doctor.id);
        const revenue = doctorPatients.reduce((sum, p) => {
          return sum + getPatientRevenue(p);
        }, 0);
        return {
          doctorId: doctor.id,
          doctorName: doctor.name,
          revenue,
        };
      }).sort((a, b) => b.revenue - a.revenue);
      
      console.log('Analytics: Doctor revenue:', doctorRevenue);
      
      // Calculate revenue by date - use payment.date from payment history (most accurate)
      const dateMap = new Map<string, number>();
      patientsWithPayments.forEach(patient => {
        // Use payment history dates if available, otherwise fall back to completedAt/registeredAt
        if (patient.paymentHistory && patient.paymentHistory.length > 0) {
          // Use actual payment dates from payment history
          patient.paymentHistory.forEach((payment: any) => {
            const paymentDate = payment.date ? new Date(payment.date).toISOString().split('T')[0] : null;
        if (paymentDate) {
              const currentRevenue = dateMap.get(paymentDate) || 0;
              dateMap.set(paymentDate, currentRevenue + (Number(payment.amount) || 0));
            }
          });
        } else {
          // Fallback: use completedAt or registeredAt if no payment history
          const fallbackDate = patient.completedAt || patient.registeredAt;
          if (fallbackDate) {
            const date = new Date(fallbackDate).toISOString().split('T')[0];
          const currentRevenue = dateMap.get(date) || 0;
            dateMap.set(date, currentRevenue + getPatientRevenue(patient));
          }
        }
      });
      
      const dateRevenue = Array.from(dateMap.entries())
        .map(([date, revenue]) => ({ date, revenue }))
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      console.log('Analytics: Date revenue:', dateRevenue);
      
      // Debug today's revenue calculation
      const today = new Date().toISOString().split('T')[0];
      const todayRevenue = dateRevenue
        .filter(item => item.date === today)
        .reduce((sum, item) => sum + item.revenue, 0);
      
      // Calculate revenue by payment method (from payment history)
      const paymentMethodMap = new Map<string, number>();
      patientsWithPayments.forEach(patient => {
        // Use payment history to get payment methods
        if (patient.paymentHistory && patient.paymentHistory.length > 0) {
          patient.paymentHistory.forEach((payment: any) => {
            const method = payment.mode || patient.paymentMode || 'Unknown';
            const currentRevenue = paymentMethodMap.get(method) || 0;
            paymentMethodMap.set(method, currentRevenue + (Number(payment.amount) || 0));
          });
        } else {
          // Fallback: use patient's payment mode if no payment history
        const method = patient.paymentMode || 'Unknown';
        const currentRevenue = paymentMethodMap.get(method) || 0;
          paymentMethodMap.set(method, currentRevenue + getPatientRevenue(patient));
        }
      });

      const paymentMethodRevenue = Array.from(paymentMethodMap.entries())
        .map(([method, revenue]) => ({ method, revenue }))
        .sort((a, b) => b.revenue - a.revenue);

      // Calculate revenue by service type (both present and previous month)
      const presentMonthServiceMap = new Map<string, { revenue: number; count: number }>();
      const previousMonthServiceMap = new Map<string, { revenue: number; count: number }>();
      
      // First, process current patient services (for patients with payments, regardless of status)
      patientsWithPayments.forEach(patient => {
        // Use payment history dates for month filtering, fallback to registeredAt
        let dateToUse: Date | null = null;
        if (patient.paymentHistory && patient.paymentHistory.length > 0) {
          // Use the most recent payment date
          const paymentDates = patient.paymentHistory
            .map((p: any) => p.date ? new Date(p.date) : null)
            .filter((d: Date | null) => d !== null && !isNaN(d.getTime()));
          if (paymentDates.length > 0) {
            dateToUse = paymentDates.sort((a, b) => b.getTime() - a.getTime())[0];
          }
        }
        if (!dateToUse && patient.registeredAt) {
          dateToUse = new Date(patient.registeredAt);
        }
        
        if (dateToUse) {
          const isPresentMonth = dateToUse.getMonth() === currentMonth.getMonth() && 
                                dateToUse.getFullYear() === currentMonth.getFullYear();
          const isPreviousMonth = dateToUse.getMonth() === previousMonth.getMonth() && 
                                 dateToUse.getFullYear() === previousMonth.getFullYear();
          
          if (isPresentMonth || isPreviousMonth) {
            const targetMap = isPresentMonth ? presentMonthServiceMap : previousMonthServiceMap;
            
            if (patient.services && patient.services.length > 0) {
              patient.services.forEach(service => {
                const current = targetMap.get(service.service_name) || { revenue: 0, count: 0 };
                targetMap.set(service.service_name, {
                  revenue: current.revenue + service.price,
                  count: current.count + 1
                });
              });
            } else {
              // If no services, use a default "General Consultation" with payment amount
              const current = targetMap.get('General Consultation') || { revenue: 0, count: 0 };
              targetMap.set('General Consultation', {
                revenue: current.revenue + getPatientRevenue(patient),
                count: current.count + 1
              });
            }
          }
        }
      });

      // Then, process bills data to include services from generated bills
      try {
        const { data: billsData, error: billsError } = await supabase
          .from('bills')
          .select('*')
          .order('created_at', { ascending: false });

        if (!billsError && billsData) {
          billsData.forEach(bill => {
            if (bill.bill_date) {
              const billDate = new Date(bill.bill_date);
              const isPresentMonth = billDate.getMonth() === currentMonth.getMonth() && 
                                    billDate.getFullYear() === currentMonth.getFullYear();
              const isPreviousMonth = billDate.getMonth() === previousMonth.getMonth() && 
                                     billDate.getFullYear() === previousMonth.getFullYear();
              
              if (isPresentMonth || isPreviousMonth) {
                const targetMap = isPresentMonth ? presentMonthServiceMap : previousMonthServiceMap;
                
                if (bill.services && bill.services.length > 0) {
                  bill.services.forEach((service: any) => {
                    const serviceName = service.service_name || service.name || 'Unknown Service';
                    const servicePrice = service.price || service.amount || 0;
                    const current = targetMap.get(serviceName) || { revenue: 0, count: 0 };
                    targetMap.set(serviceName, {
                      revenue: current.revenue + servicePrice,
                      count: current.count + 1
                    });
                  });
                } else if (bill.total_amount > 0) {
                  // If no services in bill but has total amount, use "General Consultation"
                  const current = targetMap.get('General Consultation') || { revenue: 0, count: 0 };
                  targetMap.set('General Consultation', {
                    revenue: current.revenue + (bill.total_amount || 0),
                    count: current.count + 1
                  });
                }
              }
            }
          });
        }
      } catch (error) {
        console.warn('Error fetching bills for service revenue calculation:', error);
      }

      const presentMonthServiceRevenue = Array.from(presentMonthServiceMap.entries())
        .map(([serviceName, data]) => ({ serviceName, ...data }))
        .sort((a, b) => b.revenue - a.revenue);

      const previousMonthServiceRevenue = Array.from(previousMonthServiceMap.entries())
        .map(([serviceName, data]) => ({ serviceName, ...data }))
        .sort((a, b) => b.revenue - a.revenue);

      // For backward compatibility, keep serviceRevenue as previous month
      const serviceRevenue = previousMonthServiceRevenue;

      // Calculate monthly trends (use payment dates from payment history)
      const monthlyMap = new Map<string, { revenue: number; patients: number }>();
      patientsWithPayments.forEach(patient => {
        // Use payment history dates for revenue allocation
        if (patient.paymentHistory && patient.paymentHistory.length > 0) {
          patient.paymentHistory.forEach((payment: any) => {
            if (payment.date) {
              const paymentDate = new Date(payment.date);
              const month = paymentDate.toISOString().slice(0, 7);
              const current = monthlyMap.get(month) || { revenue: 0, patients: 0 };
              monthlyMap.set(month, {
                revenue: current.revenue + (Number(payment.amount) || 0),
                patients: current.patients + (current.patients === 0 ? 1 : 0) // Count patient only once per month
              });
            }
          });
        } else {
          // Fallback: use registeredAt for patients without payment history
        if (patient.registeredAt) {
          const month = new Date(patient.registeredAt).toISOString().slice(0, 7);
          const current = monthlyMap.get(month) || { revenue: 0, patients: 0 };
          monthlyMap.set(month, {
              revenue: current.revenue + getPatientRevenue(patient),
            patients: current.patients + 1
          });
          }
        }
      });

      const monthlyTrend = Array.from(monthlyMap.entries())
        .map(([month, data]) => ({
          month: new Date(month + '-01').toLocaleString('default', { month: 'short', year: 'numeric' }),
          ...data
        }))
        .sort((a, b) => new Date(b.month).getTime() - new Date(a.month).getTime());

      // Generate real daily revenue data from actual patient data
      const dailyRevenueMap = new Map<string, { currentMonth: number; previousMonth: number }>();
      
      // Initialize daily revenue map for the current month
      const daysInCurrentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
      for (let day = 1; day <= daysInCurrentMonth; day++) {
        dailyRevenueMap.set(day.toString(), { currentMonth: 0, previousMonth: 0 });
      }
      
      // Process real patient data for daily revenue (use payment dates from payment history)
      patientsWithPayments.forEach(patient => {
        // Use payment history dates for accurate daily revenue
        if (patient.paymentHistory && patient.paymentHistory.length > 0) {
          patient.paymentHistory.forEach((payment: any) => {
            if (payment.date) {
              const paymentDate = new Date(payment.date);
              const day = paymentDate.getDate().toString();
              
              // Check if payment is from current month
              if (paymentDate.getMonth() === currentDate.getMonth() && 
                  paymentDate.getFullYear() === currentDate.getFullYear()) {
                const current = dailyRevenueMap.get(day) || { currentMonth: 0, previousMonth: 0 };
                current.currentMonth += (Number(payment.amount) || 0);
                dailyRevenueMap.set(day, current);
              }
              // Check if payment is from previous month
              else if (paymentDate.getMonth() === previousMonth.getMonth() && 
                       paymentDate.getFullYear() === previousMonth.getFullYear()) {
                const current = dailyRevenueMap.get(day) || { currentMonth: 0, previousMonth: 0 };
                current.previousMonth += (Number(payment.amount) || 0);
                dailyRevenueMap.set(day, current);
              }
            }
          });
        } else {
          // Fallback: use registeredAt for patients without payment history
        if (patient.registeredAt) {
            const patientRevenue = getPatientRevenue(patient);
          if (patientRevenue > 0) {
            const patientDate = new Date(patient.registeredAt);
            const day = patientDate.getDate().toString();
            
            // Check if patient is from current month
            if (patientDate.getMonth() === currentDate.getMonth() && 
                patientDate.getFullYear() === currentDate.getFullYear()) {
              const current = dailyRevenueMap.get(day) || { currentMonth: 0, previousMonth: 0 };
              current.currentMonth += patientRevenue;
              dailyRevenueMap.set(day, current);
            }
            // Check if patient is from previous month
            else if (patientDate.getMonth() === previousMonth.getMonth() && 
                     patientDate.getFullYear() === previousMonth.getFullYear()) {
              const current = dailyRevenueMap.get(day) || { currentMonth: 0, previousMonth: 0 };
              current.previousMonth += patientRevenue;
              dailyRevenueMap.set(day, current);
              }
            }
          }
        }
      });
      
      // Convert map to array for chart data
      const dailyRevenueData = Array.from(dailyRevenueMap.entries())
        .map(([day, data]) => ({
          day,
          currentMonth: data.currentMonth,
          previousMonth: data.previousMonth
        }))
        .sort((a, b) => parseInt(a.day) - parseInt(b.day));

      // Generate daily patient data from actual patient data
      const dailyPatientMap = new Map<string, { currentMonth: number; previousMonth: number }>();
      
      // Initialize daily patient map for the current month
      for (let day = 1; day <= daysInCurrentMonth; day++) {
        dailyPatientMap.set(day.toString(), { currentMonth: 0, previousMonth: 0 });
      }
      
      // Process real patient data for daily patient counts
      completedPatients.forEach(patient => {
        if (patient.registeredAt) {
          const patientDate = new Date(patient.registeredAt);
          const day = patientDate.getDate().toString();
          
          // Check if patient is from current month
          if (patientDate.getMonth() === currentDate.getMonth() && 
              patientDate.getFullYear() === currentDate.getFullYear()) {
            const current = dailyPatientMap.get(day) || { currentMonth: 0, previousMonth: 0 };
            current.currentMonth += 1; // Count patients, not revenue
            dailyPatientMap.set(day, current);
          }
          // Check if patient is from previous month
          else if (patientDate.getMonth() === previousMonth.getMonth() && 
                   patientDate.getFullYear() === previousMonth.getFullYear()) {
            const current = dailyPatientMap.get(day) || { currentMonth: 0, previousMonth: 0 };
            current.previousMonth += 1; // Count patients, not revenue
            dailyPatientMap.set(day, current);
          }
        }
      });
      
      // Convert map to array for chart data
      const dailyPatientData = Array.from(dailyPatientMap.entries())
        .map(([day, data]) => ({
          day,
          currentMonth: data.currentMonth,
          previousMonth: data.previousMonth
        }))
        .sort((a, b) => parseInt(a.day) - parseInt(b.day));

      // Only generate sample data if no real data exists
      if (dailyRevenueData.every(item => item.currentMonth === 0 && item.previousMonth === 0)) {
        console.log('Analytics: No real data found, generating sample data for demonstration');
        const sampleData = [];
        for (let day = 1; day <= daysInCurrentMonth; day++) {
          const baseRevenue = 2500;
          const randomFactor = 0.5 + Math.random() * 1;
          const dailyRevenue = Math.round(baseRevenue * randomFactor);
          sampleData.push({
            day: day.toString(),
            currentMonth: dailyRevenue,
            previousMonth: Math.round(dailyRevenue * 0.9) // Slightly lower for previous month
          });
        }
        dailyRevenueData.push(...sampleData);
      }

      // Generate sample patient data if no real data exists
      if (dailyPatientData.every(item => item.currentMonth === 0 && item.previousMonth === 0)) {
        console.log('Analytics: No real patient data found, generating sample data for demonstration');
        const samplePatientData = [];
        for (let day = 1; day <= daysInCurrentMonth; day++) {
          const basePatients = 8;
          const randomFactor = 0.5 + Math.random() * 1;
          const dailyPatients = Math.round(basePatients * randomFactor);
          samplePatientData.push({
            day: day.toString(),
            currentMonth: dailyPatients,
            previousMonth: Math.round(dailyPatients * 0.9) // Slightly lower for previous month
          });
        }
        dailyPatientData.push(...samplePatientData);
      }

      // Calculate growth metrics
      const lastMonth = monthlyTrend[0];
      const prevMonth = monthlyTrend[1];
      const revenueGrowth = prevMonth && lastMonth
        ? ((lastMonth.revenue - prevMonth.revenue) / prevMonth.revenue) * 100 
        : 0;
      const patientGrowth = prevMonth && lastMonth
        ? ((lastMonth.patients - prevMonth.patients) / prevMonth.patients) * 100 
        : 0;
      const avgRevenuePerPatient = patientsWithPayments.length > 0 
        ? totalRevenue / patientsWithPayments.length 
        : 0;

      // Calculate retention metrics
      const patientVisits = new Map<string, Date[]>();
      completedPatients.forEach(patient => {
        if (patient.registeredAt) {
          const visits = patientVisits.get(patient.id) || [];
          visits.push(new Date(patient.registeredAt));
          patientVisits.set(patient.id, visits);
        }
      });

      const returningPatients = Array.from(patientVisits.values())
        .filter(visits => visits.length > 1).length;
      
      const retentionRate = patientVisits.size > 0 
        ? (returningPatients / patientVisits.size) * 100 
        : 0;

      const daysBetweenVisits = Array.from(patientVisits.values())
        .filter(visits => visits.length > 1)
        .flatMap(visits => {
          const sortedVisits = [...visits].sort((a, b) => a.getTime() - b.getTime());
          return sortedVisits.slice(1).map((visit, i) => 
            differenceInDays(visit, sortedVisits[i])
          );
        });

      const avgDaysBetweenVisits = daysBetweenVisits.length 
        ? daysBetweenVisits.reduce((sum, days) => sum + days, 0) / daysBetweenVisits.length 
        : 0;

      // Calculate doctor KPIs (use patients with payments, regardless of status)
      const doctorKPIs = doctors.map(doctor => {
        const doctorPatients = patientsWithPayments.filter(p => p.doctorId === doctor.id);
        const totalDoctorRevenue = doctorPatients.reduce((sum, p) => {
          return sum + getPatientRevenue(p);
        }, 0);
        const avgRevenuePerPatient = doctorPatients.length 
          ? totalDoctorRevenue / doctorPatients.length 
          : 0;
        
        // Simulated patient satisfaction (in a real app, this would come from feedback)
        const patientSatisfaction = Math.random() * 100;
        
        // Efficiency based on average consultation time (simulated)
        const efficiency = Math.random() * 100;
        
        return {
          doctorId: doctor.id,
          doctorName: doctor.name,
          avgRevenuePerPatient,
          patientSatisfaction,
          efficiency
        };
      });

      // Calculate time-based analytics
      const hourMap = new Map<number, number>();
      const dayMap = new Map<string, number>();
      let totalWaitTime = 0;
      let waitTimeCount = 0;

      completedPatients.forEach(patient => {
        if (patient.registeredAt) {
          const date = new Date(patient.registeredAt);
          const hour = date.getHours();
          const day = format(date, 'EEEE');
          
          // Count patients by hour
          hourMap.set(hour, (hourMap.get(hour) || 0) + 1);
          
          // Count patients by day
          dayMap.set(day, (dayMap.get(day) || 0) + 1);
          
          // Calculate wait time (simulated)
          if (patient.registeredAt && patient.completedAt) {
            const waitTime = differenceInDays(
              new Date(patient.completedAt),
              new Date(patient.registeredAt)
            );
            totalWaitTime += waitTime;
            waitTimeCount++;
          }
        }
      });

      const peakHours = Array.from(hourMap.entries())
        .map(([hour, count]) => ({ hour, count }))
        .sort((a, b) => b.count - a.count);

      const peakDays = Array.from(dayMap.entries())
        .map(([day, count]) => ({ day, count }))
        .sort((a, b) => b.count - a.count);

      const avgWaitTime = waitTimeCount ? totalWaitTime / waitTimeCount : 0;

      // Simple revenue forecasting (using last 3 months average)
      const lastThreeMonths = monthlyTrend.slice(0, 3);
      const averageRevenue = lastThreeMonths.length > 0 
        ? lastThreeMonths.reduce((sum, month) => sum + month.revenue, 0) / lastThreeMonths.length 
        : 0;
      
      const revenueForecast = Array.from({ length: 3 }, (_, i) => {
        const date = new Date();
        date.setMonth(date.getMonth() + i + 1);
        return {
          date: date.toLocaleString('default', { month: 'short', year: 'numeric' }),
          forecast: averageRevenue
        };
      });
      
      const result = { 
        totalRevenue, 
        doctorRevenue, 
        dateRevenue,
        paymentMethodRevenue,
        serviceRevenue,
        presentMonthServiceRevenue, // Add present month service revenue
        previousMonthServiceRevenue, // Add previous month service revenue
        monthlyTrend,
        revenueForecast,
        dailyRevenueData, // Add daily revenue data to the result
        dailyPatientData, // Add daily patient data to the result
        growthMetrics: {
          revenueGrowth,
          patientGrowth,
          avgRevenuePerPatient
        },
        retentionMetrics: {
          returningPatients,
          retentionRate,
          avgDaysBetweenVisits
        },
        doctorKPIs,
        timeAnalytics: {
          peakHours,
          peakDays,
          avgWaitTime
        }
      };
      
      console.log('Analytics: Final result:', result);
      return result;
    } catch (error) {
      console.error('Analytics: Error in getRevenueSummary:', error);
      // Return default values if there's an error
      return {
        totalRevenue: 0,
        doctorRevenue: [],
        dateRevenue: [],
        paymentMethodRevenue: [],
        serviceRevenue: [],
        presentMonthServiceRevenue: [], // Return empty array for present month service revenue on error
        previousMonthServiceRevenue: [], // Return empty array for previous month service revenue on error
        monthlyTrend: [],
        revenueForecast: [],
        dailyRevenueData: [], // Return empty array for daily data on error
        dailyPatientData: [], // Return empty array for daily patient data on error
        growthMetrics: {
          revenueGrowth: 0,
          patientGrowth: 0,
          avgRevenuePerPatient: 0
        },
        retentionMetrics: {
          returningPatients: 0,
          retentionRate: 0,
          avgDaysBetweenVisits: 0
        },
        doctorKPIs: [],
        timeAnalytics: {
          peakHours: [],
          peakDays: [],
          avgWaitTime: 0
        }
      };
    }
  },
  
  getPatientCountByDoctor: () => {
    try {
      console.log('=== Analytics: Getting Patient Count by Doctor ===');
      
      const { patients } = usePatientsStore.getState();
      const { doctors } = useDoctorsStore.getState();
      
      console.log('Analytics: Patients count:', patients.length);
      console.log('Analytics: Doctors count:', doctors.length);
      
      const completedPatients = patients.filter(p => p.status === "completed");
      console.log('Analytics: Completed patients count:', completedPatients.length);
      
      const result = doctors.map(doctor => {
        const count = completedPatients.filter(p => p.doctorId === doctor.id).length;
        return {
          doctorId: doctor.id,
          doctorName: doctor.name,
          count,
        };
      }).sort((a, b) => b.count - a.count);
      
      console.log('Analytics: Patient count by doctor result:', result);
      return result;
    } catch (error) {
      console.error('Analytics: Error in getPatientCountByDoctor:', error);
      return [];
    }
  },
  
  getCompletedAppointmentsByDate: (startDate, endDate) => {
    try {
      console.log('=== Analytics: Getting Completed Appointments by Date ===');
      console.log('Analytics: Start date:', startDate);
      console.log('Analytics: End date:', endDate);
      
      const { patients } = usePatientsStore.getState();
      const completedPatients = patients.filter(p => p.status === "completed");
      
      console.log('Analytics: Completed patients count:', completedPatients.length);
      
      const filteredPatients = startDate && endDate
        ? completedPatients.filter(p => {
            if (!p.registeredAt) return false;
            const date = new Date(p.registeredAt);
            return date >= startDate && date <= endDate;
          })
        : completedPatients;
      
      console.log('Analytics: Filtered patients count:', filteredPatients.length);
      
      const dateMap = new Map<string, number>();
      filteredPatients.forEach(patient => {
        if (patient.registeredAt) {
          const date = new Date(patient.registeredAt).toISOString().split('T')[0];
          const currentCount = dateMap.get(date) || 0;
          dateMap.set(date, currentCount + 1);
        }
      });
      
      const result = Array.from(dateMap.entries())
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      
      console.log('Analytics: Appointments by date result:', result);
      return result;
    } catch (error) {
      console.error('Analytics: Error in getCompletedAppointmentsByDate:', error);
      return [];
    }
  },
}));
